import React, { useRef, useState, useEffect } from "react";
import { Volume2, Pause, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

/**
 * "Play aloud" button. Requests /api/audio/speak (returns MP3 bytes),
 * plays it via an <audio> element. Cached in memory for replay.
 * Uses the user's preferred voice from their profile.
 */
export default function SpeakButton({ text, voice, testId }) {
  const { user } = useAuth();
  const [state, setState] = useState("idle"); // idle | loading | playing
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const activeVoice = voice || user?.voice || "nova";

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, []);

  // Invalidate cached audio when the user changes their preferred voice.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setState("idle");
  }, [activeVoice]);

  const play = async () => {
    if (!text || !text.trim()) return;

    // Toggle: if currently playing, pause.
    if (state === "playing" && audioRef.current) {
      audioRef.current.pause();
      setState("idle");
      return;
    }

    // Re-play cached audio if we already fetched it.
    if (audioRef.current && urlRef.current) {
      try {
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
        setState("playing");
        return;
      } catch {
        /* fall through to refetch */
      }
    }

    setState("loading");
    try {
      const res = await api.post(
        "/audio/speak",
        { text: text.slice(0, 4000), voice: activeVoice },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      urlRef.current = url;
      const el = new Audio(url);
      audioRef.current = el;
      el.onended = () => setState("idle");
      el.onerror = () => setState("idle");
      await el.play();
      setState("playing");
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(detail);
      setState("idle");
    }
  };

  const Icon =
    state === "loading" ? Loader2 :
    state === "playing" ? Pause : Volume2;

  const label =
    state === "loading" ? "Loading audio…" :
    state === "playing" ? "Pause" : "Read aloud";

  return (
    <button
      type="button"
      onClick={play}
      disabled={!text || state === "loading"}
      className="btn-ghost !h-8 !px-3 focus-ring disabled:opacity-30"
      aria-label={label}
      title={label}
      data-testid={testId || "speak-btn"}
    >
      <Icon className={`w-3.5 h-3.5 ${state === "loading" ? "animate-spin" : ""}`} />
      <span className="text-[11px]">
        {state === "playing" ? "Pause" : state === "loading" ? "…" : "Listen"}
      </span>
    </button>
  );
}
